import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, session } from '../lib/api'
import { useApi } from '../lib/hooks'
import { dateTime, money, num, shortDate, titleCase } from '../lib/format'
import { Banner, Empty, ErrorNote, Loading, Panel, Pill, StatMini } from '../ui/kit'
import { FormDrawer, type FormFieldDef } from '../ui/FormDrawer'
import { useEnumOptions } from '../lib/enums'

interface OrderRow {
  id: string; code: string; status: string; channel: string
  orderedAt: string; cutoffAt: string; deliveryDate: string; pastCutoff: boolean
  buyerId: string; buyerName: string; buyerType: string; creditState: string
  corridorName: string | null
  orderedKg: number; deliveredKg: number; total: number
  fillRatePct: number | null; lineCount: number; allocatedPct: number | null
  /** Who is putting it together, and when it was flagged for pickup. */
  pickerName: string | null; readyAt: string | null
}

interface PlanRow {
  cropId: string; cropName: string; gradeId: string; gradeCode: string
  neededKg: number; buyers: number; availableKg: number
  toBuyKg: number; targetShrinkPct: number; toBuyGrossKg: number
  boardBuyPrice: number | null
}

interface Buyer {
  id: string; name: string; buyerType: string; creditState: string
  corridorId: string | null
}

interface Grade { id: string; code: string; name: string; isSellable: boolean }
interface Crop { id: string; name: string; grades: Grade[] }
interface Depot { id: string; code: string; name: string }
interface Bundle { crops: Crop[]; depots: Depot[] }

interface OrderLine {
  id: string; cropId: string; cropName: string; gradeId: string; gradeCode: string
  orderedKg: number; allocatedKg: number; unitPrice: number | null
}
interface OrderDetail { id: string; code: string; status: string; buyerName: string; lines: OrderLine[] }

interface Lot {
  id: string; code: string; status: string; cropId: string; gradeId: string
  cropName: string; gradeCode: string; farmgateKg: number; depotInKg: number | null
  allocatedKg: number | null; farmerName: string; expiringSoon: boolean; fifoBatch: string
}

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'crit' | 'info' | 'neutral'> = {
  draft: 'neutral', confirmed: 'info', allocated: 'ok', picking: 'warn', ready: 'ok',
  dispatched: 'ok', delivered: 'ok', closed: 'neutral', cancelled: 'crit',
}

/** A draft line being typed into the new-order form. */
interface DraftLine { cropId: string; gradeId: string; orderedKg: string; unitPrice: string }

export function Orders() {
  // Writing to the order book is sales's job; ops can do it too because ops is the
  // one awake at the cutoff. Everyone else reads.
  const canWrite = session.has('admin', 'sales', 'ops')

  // A buyer can be pinned from the buyer list, so "what have they ordered" is a link
  // rather than a search through every order.
  const [params, setParams] = useSearchParams()
  const buyerId = params.get('buyerId') ?? ''
  const buyerName = params.get('buyerName') ?? ''

  const orders = useApi<OrderRow[]>(
    `/orders${buyerId ? `?buyerId=${buyerId}` : ''}`, { deps: [buyerId] })
  const plan = useApi<PlanRow[]>('/orders/buying-plan')

  const [creating, setCreating] = useState(false)
  const [allocating, setAllocating] = useState<OrderRow | null>(null)
  const [handing, setHanding] = useState<OrderRow | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)

  const rows = orders.data ?? []
  const open = rows.filter((o) => ['draft', 'confirmed', 'allocated'].includes(o.status))
  const pastCutoffUnallocated = open.filter((o) => o.pastCutoff && (o.allocatedPct ?? 0) < 100)

  function reload() {
    orders.reload()
    plan.reload()
  }

  async function confirm(o: OrderRow) {
    if (!window.confirm(
      `Confirm ${o.code} for ${o.buyerName}?\n\n` +
      'Once confirmed this goes onto tomorrow\'s shopping list, and the agents will be ' +
      'sent out to buy it.',
    )) return
    try {
      await api.post(`/orders/${o.id}/confirm`)
      setBanner({ tone: 'ok', text: `${o.code} confirmed. It is on the buying plan now.` })
      reload()
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof Error ? e.message : 'That did not work.' })
    }
  }

  return (
    <main className="page">
      <div className="page-head">
        <h1>Orders</h1>
        <p>
          What buyers have asked for. Orders confirmed before the day's deadline become tomorrow's
          shopping list for the agents. Buy what you have already sold, not the other way round.
        </p>
      </div>

      <div className="stack">
        {banner && (
          <Banner tone={banner.tone === 'ok' ? 'info' : 'crit'} title={banner.text}>{null}</Banner>
        )}

        {buyerId && (
          <div className="filter-pin">
            <span>Showing only <b>{buyerName || 'one buyer'}</b></span>
            <button className="btn quiet" style={{ minHeight: 26, fontSize: 'var(--fs-xs)' }}
              onClick={() => setParams({})}>
              Show all orders
            </button>
          </div>
        )}

        {pastCutoffUnallocated.length > 0 && (
          <Banner tone="warn" title={`${pastCutoffUnallocated.length} order(s) past the deadline without enough stock set aside`}>
            Set aside stock for these, or ring the buyer now. Telling someone you will be short does
            far less damage than them finding out at the door.
          </Banner>
        )}

        <Panel
          title="What to buy tomorrow"
          meta="what buyers ordered, less what is already in the store, plus a little for the weight you expect to lose"
        >
          {plan.loading && <Loading rows={3} />}
          {plan.error && <ErrorNote error={plan.error} onRetry={plan.reload} />}
          {plan.data?.length === 0 && (
            <Empty title="Nothing to buy yet">
              Confirm tomorrow's orders and this becomes the agents' shopping list, with a little
              extra added for the weight normally lost on the way.
            </Empty>
          )}
          {plan.data && plan.data.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Crop</th><th>Grade</th><th className="n">Needed</th>
                    <th className="n">At depot</th><th className="n">Buy, net</th>
                    <th className="n">Buy, with allowance</th><th className="n">Your price</th>
                    <th className="n">Budget</th><th className="n">Buyers</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.data.map((p) => (
                    <tr key={`${p.cropId}-${p.gradeId}`}>
                      <td className="strong">{p.cropName}</td>
                      <td><Pill dot={false}>{p.gradeCode}</Pill></td>
                      <td className="n">{num(p.neededKg, 1)} kg</td>
                      <td className="n faint">{num(p.availableKg, 1)} kg</td>
                      <td className="n">{num(p.toBuyKg, 1)} kg</td>
                      <td className="n strong">
                        {num(p.toBuyGrossKg, 1)} kg
                        <span className="faint"> +{p.targetShrinkPct}%</span>
                      </td>
                      <td className="n">{money(p.boardBuyPrice, { currency: false })}</td>
                      <td className="n">
                        {p.boardBuyPrice
                          ? money(Number(p.toBuyGrossKg) * Number(p.boardBuyPrice), { compact: true })
                          : '—'}
                      </td>
                      <td className="n faint">{p.buyers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel
          title="Orders"
          meta={canWrite && (
            <button className="btn" onClick={() => setCreating(true)}>New order</button>
          )}
          footer={
            <>
              <StatMini label="Open orders" value={open.length} />
              <StatMini label="Ordered" value={`${num(rows.reduce((s, o) => s + Number(o.orderedKg ?? 0), 0))} kg`} />
              <StatMini label="Value" value={money(rows.reduce((s, o) => s + Number(o.total ?? 0), 0), { compact: true })} />
              <StatMini label="Past the deadline, no stock set aside" value={pastCutoffUnallocated.length} />
            </>
          }
        >
          {orders.error && <ErrorNote error={orders.error} onRetry={orders.reload} />}
          {orders.loading && <Loading rows={5} />}
          {orders.data?.length === 0 && (
            <Empty
              title="No orders yet"
              action={canWrite ? <button className="btn" onClick={() => setCreating(true)}>New order</button> : undefined}
            >
              Write down what a buyer has asked for. The moment you confirm it, it joins tomorrow's
              shopping list.
            </Empty>
          )}
          {rows.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Order</th><th>Buyer</th><th>Area</th>
                    <th className="n">Delivery</th><th className="n">Deadline</th>
                    <th className="n">Ordered</th><th className="n">Set aside</th>
                    <th className="n">Value</th><th>Status</th>
                    {canWrite && <th style={{ width: 150 }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => {
                    const unallocated = o.pastCutoff && (o.allocatedPct ?? 0) < 100 &&
                      ['draft', 'confirmed'].includes(o.status)
                    return (
                      <tr key={o.id} className={unallocated ? 'attention' : ''}>
                        <td className="code strong">
                          {o.code}
                          {/* Worth seeing at a glance now that buyers order for
                              themselves: nobody here typed this one. */}
                          {o.channel === 'portal' && (
                            <> <Pill tone="info" dot={false}>they ordered it</Pill></>
                          )}
                        </td>
                        <td className="truncate">
                          {o.buyerName}
                          {o.creditState === 'on_hold' && <> <Pill tone="crit" dot={false}>on hold</Pill></>}
                        </td>
                        <td className="truncate faint">{o.corridorName ?? '—'}</td>
                        <td className="n">{shortDate(o.deliveryDate)}</td>
                        <td className="n">
                          <span className={o.pastCutoff ? 'faint' : ''}>{dateTime(o.cutoffAt).slice(-5)}</span>
                          {o.pastCutoff && <> <Pill dot={false}>passed</Pill></>}
                        </td>
                        <td className="n">{num(o.orderedKg, 1)} kg</td>
                        <td className="n">
                          {o.allocatedPct === null ? '—' : `${o.allocatedPct}%`}
                        </td>
                        <td className="n">{money(o.total, { currency: false })}</td>
                        <td>
                          <Pill tone={STATUS_TONE[o.status] ?? 'neutral'}>
                            {o.status === 'ready' ? 'Ready for pickup' : titleCase(o.status)}
                          </Pill>
                          {o.pickerName && o.status !== 'dispatched' && (
                            <div className="faint" style={{ fontSize: 'var(--fs-2xs)' }}>
                              {o.status === 'ready' ? 'packed by ' : 'with '}{o.pickerName}
                            </div>
                          )}
                        </td>
                        {canWrite && (
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {o.status === 'draft' && (
                              <button className="btn quiet" style={btn} onClick={() => void confirm(o)}>
                                Confirm
                              </button>
                            )}
                            {['confirmed', 'allocated'].includes(o.status) && (
                              <button className="btn quiet" style={btn} onClick={() => setAllocating(o)}>
                                Set aside
                              </button>
                            )}
                            {o.status === 'allocated' && (
                              <> <button className="btn" style={btn} onClick={() => setHanding(o)}>
                                Send to packing
                              </button></>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {creating && (
        <NewOrder
          onClose={() => setCreating(false)}
          onDone={(code) => {
            setCreating(false)
            setBanner({ tone: 'ok', text: `${code} created as a draft. Confirm it to put it on the buying plan.` })
            reload()
          }}
        />
      )}

      {handing && (
        <SendToPacking
          order={handing}
          onClose={() => setHanding(null)}
          onDone={(who) => {
            setHanding(null)
            setBanner({ tone: 'ok', text: `${handing.code} is with ${who} to pack.` })
            reload()
          }}
        />
      )}

      {allocating && (
        <Allocate
          order={allocating}
          onClose={() => setAllocating(null)}
          onDone={(n) => {
            setAllocating(null)
            setBanner({ tone: 'ok', text: `${n} lot(s) allocated to ${allocating.code}.` })
            reload()
          }}
        />
      )}
    </main>
  )
}

// ---------------------------------------------------------------- new order
function NewOrder({ onClose, onDone }: { onClose: () => void; onDone: (code: string) => void }) {
  const buyers = useApi<Buyer[]>('/buyers')
  const ref = useApi<Bundle>('/reference/bundle', { cacheKey: 'reference.bundle' })
  const channels = useEnumOptions('order_channel', {
    app: 'In the app', standing: 'Standing order',
  })

  const [values, setValues] = useState<Record<string, unknown>>({
    deliveryDate: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    channel: 'phone',
  })
  const [lines, setLines] = useState<DraftLine[]>([
    { cropId: '', gradeId: '', orderedKg: '', unitPrice: '' },
  ])

  const crops = ref.data?.crops ?? []
  const set = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }))

  const fields: FormFieldDef[] = [
    {
      name: 'buyerId', label: 'Buyer', type: 'select', required: true,
      options: (buyers.data ?? []).map((b) => ({
        value: b.id,
        label: `${b.name}${b.creditState === 'on_hold' ? ' — ON HOLD' : ''}`,
      })),
    },
    { name: 'deliveryDate', label: 'Delivery date', type: 'date', required: true, half: true },
    {
      name: 'cutoffAt', label: 'Order deadline', type: 'datetime', half: true,
      hint: 'Leave empty to use your usual deadline',
    },
    { name: 'channel', label: 'Channel', type: 'select', half: true, options: channels },
    {
      name: 'depotId', label: 'Fulfil from', type: 'select', half: true,
      options: (ref.data?.depots ?? []).map((d) => ({ value: d.id, label: d.name })),
    },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ]

  const total = lines.reduce(
    (s, l) => s + (Number(l.orderedKg) || 0) * (Number(l.unitPrice) || 0), 0,
  )
  const totalKg = lines.reduce((s, l) => s + (Number(l.orderedKg) || 0), 0)

  const buyerOnHold = (buyers.data ?? []).find((b) => b.id === values.buyerId)?.creditState === 'on_hold'

  async function submit() {
    const usable = lines.filter((l) => l.cropId && l.gradeId && Number(l.orderedKg) > 0)
    if (usable.length === 0) throw new Error('Add at least one line with a crop, a grade and a weight.')

    const res = await api.post<{ code: string }>('/orders', {
      buyerId: values.buyerId,
      deliveryDate: values.deliveryDate,
      cutoffAt: values.cutoffAt ? new Date(String(values.cutoffAt)).toISOString() : null,
      channel: values.channel || null,
      depotId: values.depotId || null,
      notes: values.notes || null,
      lines: usable.map((l) => ({
        cropId: l.cropId,
        gradeId: l.gradeId,
        orderedKg: Number(l.orderedKg),
        unitPrice: l.unitPrice ? Number(l.unitPrice) : null,
      })),
    })
    onDone(res.code)
  }

  return (
    <FormDrawer
      wide
      kind="New"
      title="Order"
      submitLabel="Create draft"
      fields={fields}
      values={values}
      onChange={set}
      onSubmit={submit}
      onClose={onClose}
      footer={<>{num(totalKg, 0)} kg · <b>{money(total)}</b></>}
    >
      {buyerOnHold && (
        <Banner tone="warn" title="This buyer is on hold">
          You can still write the order down. Whether anything is actually allowed to leave for
          them is checked against their credit limit later.
        </Banner>
      )}

      <div className="micro" style={{ marginTop: 'var(--sp-7)', marginBottom: 'var(--sp-3)' }}>
        What they are buying
      </div>

      {lines.map((l, i) => {
        const crop = crops.find((c) => c.id === l.cropId)
        return (
          <div className="line-row" key={i}>
            <div className="grow">
              <div className="field">
                <label>Crop</label>
                <select className="input" value={l.cropId}
                  onChange={(e) => setLines((s) => s.map((x, j) =>
                    j === i ? { ...x, cropId: e.target.value, gradeId: '' } : x))}>
                  <option value="">— choose —</option>
                  {crops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grow">
              <div className="field">
                <label>Grade</label>
                <select className="input" value={l.gradeId} disabled={!crop}
                  onChange={(e) => setLines((s) => s.map((x, j) =>
                    j === i ? { ...x, gradeId: e.target.value } : x))}>
                  <option value="">— choose —</option>
                  {(crop?.grades ?? []).filter((g) => g.isSellable).map((g) => (
                    <option key={g.id} value={g.id}>{g.code} — {g.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="narrow">
              <div className="field">
                <label>Kg</label>
                <input className="input n" type="number" step="0.1" value={l.orderedKg}
                  onChange={(e) => setLines((s) => s.map((x, j) =>
                    j === i ? { ...x, orderedKg: e.target.value } : x))} />
              </div>
            </div>
            <div className="narrow">
              <div className="field">
                <label>Price</label>
                <input className="input n" type="number" step="0.01" value={l.unitPrice}
                  onChange={(e) => setLines((s) => s.map((x, j) =>
                    j === i ? { ...x, unitPrice: e.target.value } : x))} />
              </div>
            </div>
            <button type="button" className="line-drop" aria-label="Remove line"
              onClick={() => setLines((s) => s.filter((_, j) => j !== i))}>✕</button>
          </div>
        )
      })}

      <button type="button" className="line-add"
        onClick={() => setLines((s) => [...s, { cropId: '', gradeId: '', orderedKg: '', unitPrice: '' }])}>
        + Add a line
      </button>

      <p className="hint" style={{ marginTop: 'var(--sp-6)' }}>
        Leave a price empty and today's selling price for that grade is used.
      </p>
    </FormDrawer>
  )
}

// ---------------------------------------------------------------- allocate
function Allocate({ order, onClose, onDone }: {
  order: OrderRow
  onClose: () => void
  onDone: (n: number) => void
}) {
  const detail = useApi<OrderDetail>(`/orders/${order.id}`)
  // Only what is actually at a depot can be promised to a buyer. A lot still at the
  // farmgate has not been weighed in yet, and its second weight is the one that counts.
  const lots = useApi<Lot[]>('/collections/lots?status=at_depot')

  const [picked, setPicked] = useState<Record<string, string>>({})
  const key = (lineId: string, lotId: string) => `${lineId}|${lotId}`

  const available = useMemo(() => {
    const map = new Map<string, Lot[]>()
    for (const l of lots.data ?? []) {
      const k = `${l.cropId}|${l.gradeId}`
      if (!map.has(k)) map.set(k, [])
      map.get(k)!.push(l)
    }
    // Oldest batch first: produce does not wait, and the FIFO order is the one that
    // keeps the depot from quietly ageing out.
    for (const v of map.values()) v.sort((a, b) => a.fifoBatch.localeCompare(b.fifoBatch))
    return map
  }, [lots.data])

  const allocations = Object.entries(picked)
    .map(([k, kg]) => ({ lineId: k.split('|')[0], lotId: k.split('|')[1], kg: Number(kg) }))
    .filter((a) => a.kg > 0)

  async function submit() {
    if (allocations.length === 0) throw new Error('Nothing picked yet.')
    await api.post('/orders/allocate', {
      allocations: allocations.map((a) => ({
        orderLineId: a.lineId, lotId: a.lotId, allocatedKg: a.kg,
      })),
    })
    onDone(allocations.length)
  }

  return (
    <FormDrawer
      wide
      kind="Allocate"
      title={`${order.code} · ${order.buyerName}`}
      submitLabel={`Allocate ${allocations.length || ''}`.trim()}
      values={{}}
      onChange={() => {}}
      onSubmit={submit}
      onClose={onClose}
      footer={<>{num(allocations.reduce((s, a) => s + a.kg, 0), 1)} kg picked</>}
    >
      {detail.loading && <Loading rows={4} />}
      {detail.error && <ErrorNote error={detail.error} onRetry={detail.reload} />}

      {lots.data?.length === 0 && (
        <Banner tone="warn" title="Nothing in the store to set aside">
          Produce can only be promised to a buyer once it has reached a store and been weighed
          there. Until then the only weight you have is the one from the farm.
        </Banner>
      )}

      {detail.data?.lines.map((line) => {
        const pool = available.get(`${line.cropId}|${line.gradeId}`) ?? []
        const short = Number(line.orderedKg) - Number(line.allocatedKg)
        const pickedForLine = pool.reduce(
          (s, lot) => s + (Number(picked[key(line.id, lot.id)]) || 0), 0)

        return (
          <div key={line.id} style={{ marginBottom: 'var(--sp-8)' }}>
            <div className="spread" style={{ marginBottom: 'var(--sp-4)' }}>
              <b>{line.cropName} <Pill dot={false}>{line.gradeCode}</Pill></b>
              <span className="micro">
                {num(line.allocatedKg, 1)} of {num(line.orderedKg, 1)} kg set aside
                {short > 0 && <span className="warn-t"> · {num(short, 1)} kg still needed</span>}
              </span>
            </div>

            {pool.length === 0 ? (
              <p className="hint">No {line.cropName} {line.gradeCode} at a depot right now.</p>
            ) : (
              pool.map((lot) => {
                const free = Number(lot.depotInKg ?? lot.farmgateKg) - Number(lot.allocatedKg ?? 0)
                return (
                  <div className="line-row" key={lot.id}>
                    <div className="grow">
                      <b className="code">{lot.code}</b>
                      <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                        {lot.farmerName} · batch {lot.fifoBatch} · {num(free, 1)} kg free
                        {lot.expiringSoon && <span className="warn-t"> · expiring</span>}
                      </div>
                    </div>
                    <div className="narrow">
                      <input
                        className="input n" type="number" step="0.1" min="0" max={free}
                        placeholder="0"
                        value={picked[key(line.id, lot.id)] ?? ''}
                        onChange={(e) => setPicked((s) => ({ ...s, [key(line.id, lot.id)]: e.target.value }))}
                      />
                    </div>
                  </div>
                )
              })
            )}

            {pickedForLine > short && short >= 0 && (
              <p className="hint warn-t">
                That is {num(pickedForLine - short, 1)} kg more than this order still needs.
              </p>
            )}
          </div>
        )
      })}
    </FormDrawer>
  )
}

// ---------------------------------------------------------------- send to packing
/** Handing an order to a named person is the point — "it was packed short" needs an answer. */
function SendToPacking({ order, onClose, onDone }: {
  order: OrderRow
  onClose: () => void
  onDone: (who: string) => void
}) {
  const packers = useApi<Array<{ id: string; fullName: string; packing: number }>>('/picking/packers')
  const depots = useApi<{ depots: Array<{ id: string; name: string }> }>(
    '/reference/bundle', { cacheKey: 'reference.bundle' })
  const [values, setValues] = useState<Record<string, unknown>>({})
  const set = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }))

  const list = packers.data ?? []
  const chosen = list.find((p) => p.id === values.pickerUserId)

  const fields: FormFieldDef[] = [
    {
      name: 'pickerUserId', label: 'Who is packing it', type: 'select', required: true,
      options: list.map((p) => ({
        value: p.id,
        label: `${p.fullName}${p.packing > 0 ? ` — ${p.packing} on the go` : ''}`,
      })),
    },
    {
      name: 'depotId', label: 'Which store', type: 'select',
      options: (depots.data?.depots ?? []).map((d) => ({ value: d.id, label: d.name })),
    },
  ]

  async function submit() {
    await api.post(`/picking/${order.id}/assign`, {
      pickerUserId: values.pickerUserId,
      depotId: values.depotId || null,
    })
    onDone(chosen?.fullName ?? 'them')
  }

  return (
    <FormDrawer
      kind="Send to packing" title={`${order.code} · ${order.buyerName}`}
      submitLabel="Hand it over"
      fields={fields} values={values} onChange={set}
      onSubmit={submit} onClose={onClose}
      footer={<>{num(order.orderedKg, 0)} kg for {shortDate(order.deliveryDate)}</>}
    >
      <p className="hint" style={{ marginTop: 'var(--sp-6)' }}>
        It appears on their phone under <b>To pack</b>. They weigh what actually goes in
        the crate and flag it for pickup when it is standing by the door.
      </p>
    </FormDrawer>
  )
}

const btn = { minHeight: 26, padding: '2px 10px', fontSize: 'var(--fs-xs)' } as const
